import { useEffect, useMemo, useState } from 'react'
import { AssignmentPaletteEntity } from './assignmentPaletteEntities'

export type SpecificCharacterFilterRole = 'tank' | 'heal' | 'melee' | 'ranged'

export type SpecificCharacterFilters = {
  roles: Record<SpecificCharacterFilterRole, boolean>
  enabledTeamRankIds?: number[]
}

export const SPECIFIC_CHARACTER_FILTER_ROLES: SpecificCharacterFilterRole[] = [
  'tank',
  'heal',
  'melee',
  'ranged',
]

const createDefaultSpecificCharacterFilters = (): SpecificCharacterFilters => ({
  roles: {
    tank: true,
    heal: true,
    melee: true,
    ranged: true,
  },
})

const parseSpecificCharacterFilters = (
  raw: string | null,
): SpecificCharacterFilters => {
  if (!raw) return createDefaultSpecificCharacterFilters()
  try {
    const parsed = JSON.parse(raw) as Partial<SpecificCharacterFilters>
    const hasValidStoredRankIds =
      Array.isArray(parsed.enabledTeamRankIds) &&
      parsed.enabledTeamRankIds.every((value) => typeof value === 'number')
    return {
      roles: {
        tank: parsed.roles?.tank ?? true,
        heal: parsed.roles?.heal ?? true,
        melee: parsed.roles?.melee ?? true,
        ranged: parsed.roles?.ranged ?? true,
      },
      enabledTeamRankIds: hasValidStoredRankIds
        ? parsed.enabledTeamRankIds
        : undefined,
    }
  } catch {
    return createDefaultSpecificCharacterFilters()
  }
}

export const groupPaletteEntitiesByCategory = (
  paletteEntities: AssignmentPaletteEntity[],
) => {
  const categories: Record<
    AssignmentPaletteEntity['category'],
    AssignmentPaletteEntity[]
  > = {
    collection: [],
    group: [],
    specific_character: [],
    by_role: [],
    by_class: [],
  }
  paletteEntities.forEach((entry) => {
    categories[entry.category].push(entry)
  })
  return categories
}

export const getSourceCategoryEntries = (
  hasSpecificCharacters: boolean,
  order: 'dialog' | 'sidebar' = 'dialog',
): Array<[AssignmentPaletteEntity['category'], string]> => {
  const collection: [AssignmentPaletteEntity['category'], string] = [
    'collection',
    'dashboard.team.plans.assignments.categories.collection',
  ]
  const group: [AssignmentPaletteEntity['category'], string] = [
    'group',
    'dashboard.team.plans.assignments.categories.group',
  ]
  const specificCharacter: [AssignmentPaletteEntity['category'], string] = [
    'specific_character',
    'dashboard.team.plans.assignments.categories.specific-character',
  ]
  const byRole: [AssignmentPaletteEntity['category'], string] = [
    'by_role',
    'dashboard.team.plans.assignments.categories.by-role',
  ]
  const byClass: [AssignmentPaletteEntity['category'], string] = [
    'by_class',
    'dashboard.team.plans.assignments.categories.by-class',
  ]
  if (order === 'sidebar') {
    return [
      byRole,
      byClass,
      ...(hasSpecificCharacters ? [specificCharacter] : []),
      group,
      collection,
    ]
  }
  return [
    collection,
    group,
    ...(hasSpecificCharacters ? [specificCharacter] : []),
    byRole,
    byClass,
  ]
}

const useSpecificCharacterFilters = (
  storageKey: string,
  specificCharacterEntries: AssignmentPaletteEntity[],
) => {
  const [specificCharacterFilters, setSpecificCharacterFilters] =
    useState<SpecificCharacterFilters>(() => {
      if (typeof window === 'undefined') {
        return createDefaultSpecificCharacterFilters()
      }
      return parseSpecificCharacterFilters(localStorage.getItem(storageKey))
    })

  useEffect(() => {
    if (typeof window === 'undefined') return
    setSpecificCharacterFilters(
      parseSpecificCharacterFilters(localStorage.getItem(storageKey)),
    )
  }, [storageKey])

  useEffect(() => {
    if (typeof window === 'undefined') return
    localStorage.setItem(storageKey, JSON.stringify(specificCharacterFilters))
  }, [specificCharacterFilters, storageKey])

  const availableTeamRanks = useMemo(() => {
    const ranksById = new Map<
      number,
      { id: number; name: string; defaultVisible: boolean }
    >()
    specificCharacterEntries.forEach((entry) => {
      const rankId = entry.specificCharacterFilter?.teamRankId
      if (rankId === null || rankId === undefined) return
      const existing = ranksById.get(rankId)
      const isDefaultVisible =
        Boolean(entry.specificCharacterFilter?.raidVisible) &&
        !entry.specificCharacterFilter?.forAlts
      if (!existing) {
        ranksById.set(rankId, {
          id: rankId,
          name: entry.specificCharacterFilter?.teamRankName ?? 'Unknown',
          defaultVisible: isDefaultVisible,
        })
        return
      }
      if (isDefaultVisible) {
        existing.defaultVisible = true
      }
    })
    return [...ranksById.values()].sort((left, right) =>
      left.name.localeCompare(right.name),
    )
  }, [specificCharacterEntries])

  const defaultEnabledTeamRankIds = useMemo(
    () =>
      availableTeamRanks
        .filter((rank) => rank.defaultVisible)
        .map((rank) => rank.id),
    [availableTeamRanks],
  )
  const enabledTeamRankIds = useMemo(
    () =>
      specificCharacterFilters.enabledTeamRankIds ?? defaultEnabledTeamRankIds,
    [defaultEnabledTeamRankIds, specificCharacterFilters.enabledTeamRankIds],
  )
  const enabledTeamRankIdSet = useMemo(
    () => new Set(enabledTeamRankIds),
    [enabledTeamRankIds],
  )
  const hasSpecificCharacterFilterOverrides =
    Object.values(specificCharacterFilters.roles).some((enabled) => !enabled) ||
    enabledTeamRankIds.length !== defaultEnabledTeamRankIds.length ||
    enabledTeamRankIds.some(
      (rankId) => !defaultEnabledTeamRankIds.includes(rankId),
    )

  const isSpecificCharacterVisible = (entry: AssignmentPaletteEntity) => {
    const role = entry.specificCharacterFilter?.role
    const isRoleVisible = role ? specificCharacterFilters.roles[role] : true
    if (!isRoleVisible) return false
    const rankId = entry.specificCharacterFilter?.teamRankId
    if (rankId === null || rankId === undefined) return false
    return enabledTeamRankIdSet.has(rankId)
  }

  return {
    specificCharacterFilters,
    setSpecificCharacterFilters,
    availableTeamRanks,
    defaultEnabledTeamRankIds,
    enabledTeamRankIdSet,
    hasSpecificCharacterFilterOverrides,
    isSpecificCharacterVisible,
  }
}

export default useSpecificCharacterFilters
